import React, { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	ArrowRightIcon,
	ChevronLeftIcon,
	GripVerticalIcon,
	InfoIcon,
	LinkIcon,
	RefreshCcw,
	SettingsIcon,
} from 'lucide-react'
import { useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import { getFieldIcon } from '@/shared/components/query-builder/helpers'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { Field, FieldLabel } from '@/shared/components/ui/field'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseAdornmentButton,
	InputBaseControl,
	InputBaseInput,
} from '@/shared/components/ui/input-base'
import { Label } from '@/shared/components/ui/label'
import { Loader } from '@/shared/components/ui/loader'
import {
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import {
	Sortable,
	SortableContent,
	SortableItem,
	SortableItemHandle,
	SortableOverlay,
} from '@/shared/components/ui/sortable'
import { ampli } from '@/shared/lib/ampli'

import { getReferenceFields, getSchemaTypeString } from '../lib/helpers'
import { useExportConfiguration } from './ExportConfigurationProvider'

type ExportFieldsModalContentProps = {
	isLoading?: boolean
	onClose(): void
	onSubmit(data: ExportFieldsFormData): void
	onBack(): void
	defaultValues: ExportFieldsFormData
}
export function ExportFieldsModalContent({
	isLoading,
	onClose,
	onSubmit,
	onBack,
	defaultValues,
}: ExportFieldsModalContentProps) {
	const { t } = useTranslation()

	const { kind } = useExportConfiguration()
	useEffect(() => {
		ampli.exportParamsFieldsConfigurationOpen({
			export_kind: kind,
		})
	}, [])
	const methods = useForm<ExportFieldsFormData>({
		defaultValues,
		resolver: zodResolver(ExportFieldsSchema),
	})
	const handleSubmit = methods.handleSubmit(onSubmit)
	if (isLoading) {
		return (
			<ModalContent data-testid='export-data-modal-form'>
				<div className='flex items-center justify-center py-44'>
					<Loader />
				</div>
			</ModalContent>
		)
	}

	return (
		<ModalContent data-testid='export-data-modal-form' className='max-w-5xl'>
			<ModalHeader>
				<ModalTitle>
					{t('export.fields.modal_title', {
						defaultValue: 'Define columns to be downloaded',
					})}
				</ModalTitle>
			</ModalHeader>
			<ErrorBoundary
				fallback={
					<div className='flex flex-col justify-center py-20 text-center'>
						<p className='text-lg font-bold'>{t('fatal_error_title')}🐞</p>
						<p className='text-muted-foreground'>{t('fatal_error_desc')}</p>
					</div>
				}
			>
				<ModalBody className='overflow-y-auto'>
					{methods.formState.isDirty && (
						<Alert variant='neutral' className='mb-2'>
							<InfoIcon />
							<AlertDescription>
								{t('export.fields.modal_save_custom_template_hint', {
									defaultValue:
										'Changed view will be saved as Custom columns template',
								})}
							</AlertDescription>
						</Alert>
					)}
					<FormProvider methods={methods} onSubmit={handleSubmit}>
						<div className='mr-5 mb-1 flex'>
							<div className='w-1/2 font-semibold'>
								{t('export.fields.modal_col_1', {
									defaultValue: '1. Select columns you want to download:',
								})}
							</div>
							<div className='w-1/2 font-semibold'>
								{t('export.fields.modal_col_2', {
									defaultValue: '2. Rename columns if needed:',
								})}
							</div>
						</div>
						<div className='p-1'>
							<SelectAllCheckbox />
						</div>
						<div className='-mx-4 max-h-96 overflow-y-auto px-4'>
							<ExportFieldsConfig />
						</div>
					</FormProvider>
				</ModalBody>
				<ModalFooter>
					<Button
						className='mr-auto'
						onClick={onClose}
						data-testid='export-data-modal-cancel'
					>
						{t('general_cancel')}
					</Button>
					<div className='flex items-center space-x-2'>
						<Button onClick={onBack} data-testid='export-data-modal-back'>
							<ChevronLeftIcon />
							{t('general_back')}
						</Button>
						<Button
							variant='primary'
							onClick={handleSubmit}
							data-testid='export-data-modal-submit'
						>
							{t('app.export', { defaultValue: 'Export' })}
						</Button>
					</div>
				</ModalFooter>
			</ErrorBoundary>
		</ModalContent>
	)
}

const SelectAllCheckbox = () => {
	const { t } = useTranslation()

	const { watch, setValue } = useFormContext<ExportFieldsFormData>()
	const fields = watch('fields')
	const isAllSelected = fields.every((field) => field.enabled)
	const isSomeSelected = fields.some((field) => field.enabled)
	const handleSelectAll = () => {
		setValue(
			'fields',
			fields.map((field) => ({
				...field,
				enabled: !isAllSelected,
			}))
		)
	}

	return (
		<Field orientation='horizontal'>
			<Checkbox
				id='select-all'
				checked={isAllSelected}
				indeterminate={isSomeSelected && !isAllSelected}
				onChange={() => handleSelectAll()}
			/>
			<FieldLabel htmlFor='select-all' className='font-normal'>
				{t('general_select_all', { defaultValue: 'Select all' })}
			</FieldLabel>
		</Field>
	)
}
export function ExportFieldsConfig() {
	const { fields, move } = useFieldArray({
		name: 'fields',
	})

	return (
		<Sortable
			value={fields}
			getItemValue={(item) => item.id}
			onMove={(event) => {
				const { active, over } = event
				if (over && active.id !== over?.id) {
					const activeIndex = active.data.current?.sortable?.index
					const overIndex = over.data.current?.sortable?.index
					if (activeIndex !== undefined && overIndex !== undefined) {
						ampli.columnsOrderChanged()
						move(activeIndex, overIndex)
					}
				}
			}}
		>
			<SortableContent asChild>
				<div>
					{fields.map(({ id }, index) => (
						<SortableItem key={id} value={id} asChild>
							<div className='flex items-center justify-between gap-2 p-1 outline-none'>
								<ColumnItem index={index} />
								<SortableItemHandle>
									<GripVerticalIcon className='size-4 cursor-grab text-disabled-foreground' />
								</SortableItemHandle>
							</div>
						</SortableItem>
					))}
				</div>
			</SortableContent>
			<SortableOverlay>
				{({ value }) => {
					const index = fields.findIndex((item) => item.id === value)
					if (index < 0) {
						return null
					}

					return (
						<div className='flex items-center justify-between p-1'>
							<ColumnItem index={index} />
							<span className='ml-2 flex cursor-grab items-center text-disabled'>
								<GripVerticalIcon className='size-4' />
							</span>
						</div>
					)
				}}
			</SortableOverlay>
		</Sortable>
	)
}

function ColumnItem({ index }: { index: number }) {
	const { t } = useTranslation()

	const { watch } = useFormContext()
	const key = watch(`fields.${index}.key`)
	const type = watch(`fields.${index}.type`)
	const name = watch(`fields.${index}.name`)
	const enabled = watch(`fields.${index}.enabled`)
	const reference = getReferenceFields(watch(`fields.${index}.reference`))

	return (
		<div className='flex w-full items-start'>
			<div className='flex w-1/2 pt-2'>
				<FormField
					name={`fields.${index}.enabled`}
					render={({ field: { value, onChange } }) => (
						<FormItem orientation='horizontal'>
							<FormControl>
								<Checkbox
									checked={value}
									className='flex items-center'
									onChange={(e) => {
										ampli.exportParamsFieldsConfigurationToggleVisability({
											field: key,
											enabled: !value,
										})
										onChange(e)
									}}
								/>
							</FormControl>
							<FormLabel className='ml-2 flex items-center space-x-1'>
								{reference ? (
									<LinkIcon className='size-4 text-muted-foreground' />
								) : (
									getFieldIcon({
										name: getSchemaTypeString(type),
									})
								)}
								<span className='text-sm font-medium'>{name}</span>
								<span className='text-sm text-muted-foreground'>
									({t(`type.${type}`, { defaultValue: type })})
								</span>
							</FormLabel>
						</FormItem>
					)}
				/>
				<ArrowRightIcon className='mr-2 ml-auto size-4 text-muted-foreground' />
			</div>
			<div className='flex w-1/2'>
				<FormField
					name={`fields.${index}.custom_name`}
					render={({ field: { value, onChange } }) => (
						<FormItem className='w-full'>
							<FormControl>
								<InputBase className='w-full' disabled={!enabled}>
									<InputBaseControl>
										<InputBaseInput
											className='w-full'
											value={value}
											onChange={(e) => {
												ampli.exportParamsFieldsConfigurationChangeFieldName({
													field: key,
													name: e.target.value,
												})
												onChange(e.target.value)
											}}
										/>
									</InputBaseControl>
									{value !== name && (
										<InputBaseAdornment>
											<InputBaseAdornmentButton onClick={() => onChange(name)}>
												<RefreshCcw />
											</InputBaseAdornmentButton>
										</InputBaseAdornment>
									)}
									{reference && (
										<Popover>
											<PopoverTrigger asChild>
												<InputBaseAdornment>
													<InputBaseAdornmentButton
														size='icon'
														variant='ghost'
														className='relative'
													>
														<SettingsIcon className='size-4' />
													</InputBaseAdornmentButton>
												</InputBaseAdornment>
											</PopoverTrigger>
											<PopoverContent className='w-80' align='end'>
												<div className='grid gap-4'>
													<div className='space-y-2'>
														<h4 className='leading-none font-medium'>
															{t('export.field_config.title', {
																defaultValue: 'Filed export',
															})}
														</h4>
														<p className='text-sm text-muted-foreground'>
															{t('export.field_config.subtitle', {
																defaultValue:
																	'Configure the export settings for entity related field.',
															})}
														</p>
													</div>
													<div className='grid gap-2'>
														<div className='grid grid-cols-3 items-center gap-4'>
															<Label htmlFor='width'>
																{t('export.field_config.entity', {
																	defaultValue: 'Entity',
																})}
															</Label>
															<Input
																id='width'
																disabled
																defaultValue={t(`entity.${reference.entity}`, {
																	defaultValue: reference.entity,
																})}
																className='col-span-2 h-8'
															/>
														</div>
														<FormField
															name={`fields.${index}.reference.field`}
															render={({ field: { value, onChange } }) => (
																<FormItem className='grid grid-cols-3 items-center gap-4'>
																	<FormLabel>
																		{t('export.field_config.entity_field', {
																			defaultValue: 'Entity field',
																		})}
																	</FormLabel>
																	<Select
																		defaultValue={value}
																		value={value}
																		onValueChange={(value) => {
																			ampli.exportParamsFieldsConfigurationChangeFieldRef(
																				{
																					field: key,
																					value,
																				}
																			)
																			onChange(value)
																		}}
																	>
																		<FormControl
																			data-name={`fields.${index}.reference.field`}
																		>
																			<SelectTrigger className='col-span-2 h-8 w-full'>
																				<SelectValue placeholder='Currency' />
																			</SelectTrigger>
																		</FormControl>
																		<SelectContent align='end'>
																			<SelectGroup>
																				{getEntityFields(reference.entity)?.map(
																					(field) => (
																						<SelectItem
																							key={field}
																							value={field}
																							data-value={field}
																						>
																							{field}
																						</SelectItem>
																					)
																				)}
																			</SelectGroup>
																		</SelectContent>
																	</Select>
																</FormItem>
															)}
														/>
													</div>
												</div>
											</PopoverContent>
										</Popover>
									)}
								</InputBase>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
		</div>
	)
}

const getEntityFields = (entity: string) => {
	switch (entity) {
		case 'pricing_campaign':
			return ['id', 'name']
		case 'optimization_group':
			return ['id', 'name']
		case 'brand':
			return ['id', 'name']
		case 'category':
			return ['id', 'name']
		case 'sales_entity':
			return ['id', 'name']
		case 'optimization':
			return ['status']
		case 'sales_level':
			return ['id', 'name']
		default:
			return ['id', 'name']
	}
}
export const ExportFieldsSchema = z.object({
	fields: z.array(
		z.object({
			key: z.string(),
			enabled: z.boolean(),
			name: z.string(),
			custom_name: z.string().min(1, 'Name is required'),
			static_value: z.string().nullable(),
			type: z.string(),
			reference: z
				.object({
					entity: z.string(),
					field: z.string(),
				})
				.nullable(),
		})
	),
})

export type ExportFieldsFormData = z.infer<typeof ExportFieldsSchema>
